import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import {
  PiArrowBendUpLeft,
  PiPaperclip,
  PiPaperPlaneRight,
  PiX,
} from 'react-icons/pi';
import Loader from '@/components/Loader';
import { formatDate } from '@/lib/helpers';
import { MONDAY_BOARDS, mondayItemUrl } from '@/lib/monday';
import {
  type LegalRequestType,
  type TLegalRequest,
  type TLegalRequestAsset,
  type TLegalRequestUpdate,
  useCreateLegalRequestReply,
  useLegalRequestUpdates,
  useMarkLegalRequestRead,
} from '@/models/offers';

const TYPE_COLORS: Record<LegalRequestType, string> = {
  legal_advice: 'blue',
  nda: 'violet',
  evaluation_lo: 'orange',
  contract_review: 'teal',
};

const REQUEST_TYPE_LABELS: Record<LegalRequestType, string> = {
  legal_advice: 'Legal Advice',
  nda: 'NDA',
  evaluation_lo: 'Eval LO',
  contract_review: 'Contract',
};

const STATUS_COLORS: Record<string, string> = {
  New: 'gray',
  'In Progress': 'blue',
  'Working on it': 'blue',
  Done: 'green',
  Approved: 'green',
  Rejected: 'red',
  Stuck: 'orange',
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return formatDate(dateStr);
}

function fileDownloadUrl(asset: TLegalRequestAsset): string {
  const source = asset.publicUrl || asset.url;
  return `/api/backoffice/offer/legal-request-file?url=${encodeURIComponent(source)}`;
}

function ReplyCard({
  update,
}: {
  update: Omit<TLegalRequestUpdate, 'replies'>;
}) {
  return (
    <Paper withBorder p="xs" radius="sm">
      <Group gap="xs" mb={4}>
        <Avatar
          name={update.creator.name}
          size="sm"
          radius="xl"
          color="initials"
        />
        <Text fz="xs" fw={500}>
          {update.creator.name}
        </Text>
        <Text fz="xs" c="dimmed">
          {formatRelativeTime(update.createdAt)}
        </Text>
      </Group>
      <Box
        fz="xs"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.body) }}
      />
    </Paper>
  );
}

function InlineReplyBox({
  legalRequestId,
  parentUpdateId,
  onDone,
}: {
  legalRequestId: number;
  parentUpdateId: string;
  onDone: () => void;
}) {
  const [text, setText] = useState('');
  const createReply = useCreateLegalRequestReply();

  const handleSend = () => {
    if (!text.trim()) return;
    createReply.mutate(
      { legalRequestId, body: text.trim(), parentUpdateId },
      {
        onSuccess: () => {
          setText('');
          onDone();
        },
      },
    );
  };

  return (
    <Box ml="lg" mt="xs">
      <Textarea
        placeholder="Write a reply..."
        autosize
        minRows={1}
        maxRows={4}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
      />
      <Group justify="flex-end" mt={4}>
        <Button size="compact-xs" variant="subtle" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="compact-xs"
          onClick={handleSend}
          loading={createReply.isPending}
          disabled={!text.trim()}
        >
          Reply
        </Button>
      </Group>
    </Box>
  );
}

function UpdateBlock({
  update,
  legalRequestId,
}: {
  update: TLegalRequestUpdate;
  legalRequestId: number;
}) {
  const [replying, setReplying] = useState(false);

  return (
    <div>
      <Paper withBorder p="sm" radius="sm">
        <Group gap="sm" mb="xs">
          <Avatar
            name={update.creator.name}
            size="md"
            radius="xl"
            color="initials"
          />
          <div>
            <Text fz="sm" fw={500}>
              {update.creator.name}
            </Text>
            <Text fz="xs" c="dimmed">
              {formatRelativeTime(update.createdAt)}
            </Text>
          </div>
        </Group>

        <Box
          fz="sm"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.body) }}
        />

        {(update.assets?.length ?? 0) > 0 && (
          <Group gap="xs" mt="xs">
            {update.assets.map((asset) => (
              <Anchor
                key={asset.id}
                href={fileDownloadUrl(asset)}
                target="_blank"
                fz="xs"
                download={asset.name}
              >
                {asset.name}
              </Anchor>
            ))}
          </Group>
        )}

        <Group
          gap="xs"
          mt="xs"
          style={{
            borderTop: '1px solid var(--mantine-color-default-border)',
            paddingTop: 8,
          }}
        >
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<PiArrowBendUpLeft size={12} />}
            onClick={() => setReplying(!replying)}
          >
            Reply
          </Button>
        </Group>
      </Paper>

      {update.replies.length > 0 && (
        <Stack gap="xs" ml="lg" mt="xs">
          {update.replies.map((reply) => (
            <ReplyCard key={reply.id} update={reply} />
          ))}
        </Stack>
      )}

      {replying && (
        <InlineReplyBox
          legalRequestId={legalRequestId}
          parentUpdateId={update.id}
          onDone={() => setReplying(false)}
        />
      )}
    </div>
  );
}

export default function LegalRequestDrawer({
  opened,
  onClose,
  legalRequest,
}: {
  opened: boolean;
  onClose: () => void;
  legalRequest: TLegalRequest | null;
}) {
  const { data: updates = [], isLoading: updatesLoading } =
    useLegalRequestUpdates(opened ? legalRequest?.id : undefined);
  const createReply = useCreateLegalRequestReply();
  const markRead = useMarkLegalRequestRead();

  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [StarterKit, Underline, Link.configure({ openOnClick: false })],
    content: '',
  });

  useEffect(() => {
    if (opened && legalRequest?.hasUnread) {
      markRead.mutate({
        legalRequestId: legalRequest.id,
        offerId: legalRequest.offerId,
      });
    }
  }, [opened, legalRequest?.id, legalRequest?.hasUnread]);

  const handleSend = () => {
    if (!editor || !legalRequest) return;
    const html = editor.getHTML();
    if (html === '<p></p>' && files.length === 0) return;

    createReply.mutate(
      {
        legalRequestId: legalRequest.id,
        body: html,
        files: files.length > 0 ? files : undefined,
      },
      {
        onSuccess: () => {
          editor.commands.clearContent();
          setFiles([]);
        },
      },
    );
  };

  const mondayUrl = legalRequest
    ? mondayItemUrl(MONDAY_BOARDS.LEGAL_REQUESTS, legalRequest.mondayItemId)
    : '#';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={legalRequest?.subject ?? 'Legal Request'}
      position="right"
      size="xl"
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 60px)',
          padding: 0,
        },
      }}
    >
      {legalRequest && (
        <>
          <Group gap="xs" px="md" pb="sm">
            <Badge
              size="sm"
              color={TYPE_COLORS[legalRequest.requestType]}
              variant="light"
            >
              {REQUEST_TYPE_LABELS[legalRequest.requestType]}
            </Badge>
            <Badge
              size="sm"
              color={STATUS_COLORS[legalRequest.mondayStatus] ?? 'gray'}
              variant="light"
            >
              {legalRequest.mondayStatus}
            </Badge>
            <Anchor href={mondayUrl} target="_blank" fz="xs">
              View on Monday
            </Anchor>
          </Group>
          <Text fz="xs" c="dimmed" px="md" pb="sm">
            Created by{' '}
            {legalRequest.createdByName || legalRequest.createdByEmail} &middot;{' '}
            {formatDate(legalRequest.createdAt)}
          </Text>

          <Box
            px="md"
            pb="sm"
            style={{
              borderBottom: '1px solid var(--mantine-color-default-border)',
            }}
          >
            <RichTextEditor editor={editor}>
              <RichTextEditor.Toolbar>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.BulletList />
                  <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Link />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
              <RichTextEditor.Content />
            </RichTextEditor>

            {files.length > 0 && (
              <Group gap="xs" mt="xs">
                {files.map((file, i) => (
                  <Badge
                    key={`${file.name}-${i}`}
                    variant="light"
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        onClick={() =>
                          setFiles((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      >
                        <PiX size={12} />
                      </ActionIcon>
                    }
                  >
                    {file.name}
                  </Badge>
                ))}
              </Group>
            )}

            <Group justify="space-between" mt="xs">
              <ActionIcon
                variant="subtle"
                onClick={() => fileInputRef.current?.click()}
              >
                <PiPaperclip size={18} />
              </ActionIcon>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) {
                    setFiles((prev) => [
                      ...prev,
                      ...Array.from(e.target.files!),
                    ]);
                    e.target.value = '';
                  }
                }}
              />
              <Button
                size="compact-sm"
                rightSection={<PiPaperPlaneRight size={14} />}
                onClick={handleSend}
                loading={createReply.isPending}
              >
                Send
              </Button>
            </Group>
          </Box>

          <ScrollArea flex={1} px="md">
            <Stack gap="sm" py="sm">
              {updates.map((update) => (
                <UpdateBlock
                  key={update.id}
                  update={update}
                  legalRequestId={legalRequest.id}
                />
              ))}
              {updatesLoading && <Loader />}
              {!updatesLoading && updates.length === 0 && (
                <Text fz="sm" c="dimmed" ta="center" py="xl">
                  No updates yet. Write an update above.
                </Text>
              )}
            </Stack>
          </ScrollArea>
        </>
      )}
    </Drawer>
  );
}
